/**
 * ExpenseCompactList — Vue "lignes avec tiret" pour l'onglet Achats.
 *
 * Affiche tous les achats visibles (selon le sous-onglet courant) groupés par
 * statut, sous forme de lignes compactes. Au clic sur une ligne, ouvre un
 * Dialog avec le détail complet + les actions adaptées au statut.
 *
 * Ce composant est rendu À LA PLACE des grandes cards quand l'utilisateur
 * active "Vue : lignes" depuis la toolbar.
 */
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  CheckCircle,
  Edit2,
  Trash2,
  Printer,
  X,
  Truck,
  Receipt,
  FileText,
  PackagePlus,
  Eye,
  Archive,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (p) => new Intl.NumberFormat("fr-FR").format(p || 0);

const MONTH_NAMES_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

// 🆕 Fév 2026 — Regroupement par mois d'un tableau d'achats.
// Utilise `posting_date` (date comptable des régularisations) en priorité,
// sinon `created_at`. Retourne un tableau ordonné DESC par mois (récent d'abord).
// 🆕 Fallback : les items sans date valide sont placés dans un bucket "sans_date"
// pour ne JAMAIS disparaître de l'UI (Android / anciens records).
// 🆕 Fév 2026 — Chaque bucket calcule aussi une mini-ventilation par catégorie
//   (top 3) pour affichage compact quand replié.
const groupExpensesByMonth = (items) => {
  const byKey = new Map();
  for (const e of items || []) {
    const raw = (e?.posting_date || e?.created_at || e?.approved_at || e?.completed_at || "").slice(0, 10);
    let key;
    if (raw && raw.length >= 7 && /^\d{4}-\d{2}/.test(raw)) {
      key = raw.slice(0, 7); // YYYY-MM
    } else {
      key = "sans_date";
    }
    if (!byKey.has(key)) byKey.set(key, { key, items: [], total: 0, by_category: {} });
    const b = byKey.get(key);
    b.items.push(e);
    b.total += Number(e?.amount || 0);
    const cat = (e?.category || "Autre").trim() || "Autre";
    b.by_category[cat] = (b.by_category[cat] || 0) + Number(e?.amount || 0);
  }
  // Trie les catégories par montant DESC pour chaque bucket → conservera le top-3 à l'affichage
  for (const b of byKey.values()) {
    b.top_categories = Object.entries(b.by_category)
      .sort((a, b2) => b2[1] - a[1])
      .slice(0, 3);
  }
  return Array.from(byKey.values()).sort((a, b) => {
    if (a.key === "sans_date") return 1;
    if (b.key === "sans_date") return -1;
    return b.key.localeCompare(a.key);
  });
};

const monthLabel = (ym) => {
  if (!ym || ym === "sans_date") return "Sans date";
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  return `${MONTH_NAMES_FR[m - 1]} ${y}`;
};

const STATUS_GROUPS = [
  {
    key: "pending",
    label: "À VALIDER",
    color: "purple",
    statuses: ["pending"],
    icon: AlertCircle,
  },
  {
    key: "admin_review",
    label: "EN COURS DE VALIDATION (admin)",
    color: "amber",
    statuses: ["admin_review"],
    icon: Eye,
  },
  {
    key: "revision_requested",
    label: "À RÉVISER",
    color: "orange",
    statuses: ["revision_requested"],
    icon: Edit2,
  },
  {
    key: "approved",
    label: "APPROUVÉS",
    color: "green",
    statuses: ["approved"],
    icon: CheckCircle,
  },
  {
    key: "completed",
    label: "TERMINÉS",
    color: "slate",
    statuses: ["completed"],
    icon: FileText,
  },
];

const COLOR_CLASSES = {
  purple: { border: "border-purple-500/40", text: "text-purple-300", bg: "from-purple-900/30 to-indigo-900/20" },
  amber: { border: "border-amber-500/40", text: "text-amber-300", bg: "from-amber-900/30 to-orange-900/20" },
  orange: { border: "border-orange-500/40", text: "text-orange-300", bg: "from-orange-900/30 to-rose-900/20" },
  green: { border: "border-green-500/40", text: "text-green-300", bg: "from-green-900/30 to-emerald-900/20" },
  slate: { border: "border-slate-600/50", text: "text-slate-200", bg: "from-slate-800/40 to-slate-900/30" },
};

const ExpenseCompactList = ({
  expenses,
  currentUser,
  achatsSubView,
  matchesAuthorFilter,
  isFinished,
  sortExpenses,
  // Actions
  updateExpense,
  deleteExpense,
  openExpenseForEdit,
  openReviseModal,
  convertExpenseToPO,
  printSingleExpenseTicket,
  printExpensePDF,
  allocateExpenseToAccount,
  availableAccounts,
  receiveExpenseStock,
  fetchExpenses,
  // Sélection multi (rattachement à une période)
  selectionMode = false,
  selectedIds,
  toggleSelection,
}) => {
  const [selected, setSelected] = useState(null);
  // 🆕 Fév 2026 — Mois dépliés (Set de clés composées "statusKey::YYYY-MM").
  // Vide au premier rendu → tous les mois sont REPLIÉS par défaut.
  const [openMonths, setOpenMonths] = useState(new Set());
  const toggleMonth = (compoundKey) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(compoundKey)) next.delete(compoundKey); else next.add(compoundKey);
      return next;
    });
  };
  const isAdmin = currentUser?.role === "admin";

  // Filtre principal : selon sous-onglet
  let visible = expenses;
  if (achatsSubView === "en_cours") {
    visible = expenses.filter(
      (e) =>
        (e.status === "pending" || e.status === "admin_review") &&
        (isAdmin ? matchesAuthorFilter?.(e) ?? true : true)
    );
  } else if (achatsSubView === "a_reviser") {
    visible = expenses.filter((e) => e.status === "revision_requested");
  } else if (achatsSubView === "valides") {
    // Validés + Terminés (pour Admin), Approuvés seuls pour Manager
    visible = expenses.filter(
      (e) => e.status === "approved" || (isAdmin && isFinished?.(e))
    );
  } else if (achatsSubView === "mes_demandes") {
    const me = currentUser?.full_name || currentUser?.username || "";
    visible = expenses.filter(
      (e) => e.requested_by === me || e.requested_by === currentUser?.username
    );
  } else if (achatsSubView === "achete") {
    visible = expenses.filter((e) => e.source === "appro_manager" && e.payment_mode);
  }

  // Groupement par statut
  const grouped = STATUS_GROUPS.map((g) => {
    const items = visible.filter((e) => {
      if (g.key === "completed") return isFinished?.(e);
      return g.statuses.includes(e.status);
    });
    return { ...g, items: sortExpenses ? sortExpenses(items) : items };
  }).filter((g) => g.items.length > 0);

  const formatDateLine = (e) => {
    const raw = e?.completed_at || e?.approved_at || e?.created_at || "";
    try { return raw ? format(parseISO(raw), "dd/MM", { locale: fr }) : ""; }
    catch { return ""; }
  };

  if (visible.length === 0) {
    return (
      <Card className="bg-slate-800/30 border-slate-700">
        <CardContent className="py-8 text-center text-slate-500 text-sm">
          Aucune demande dans cette vue.
        </CardContent>
      </Card>
    );
  }

  const selectedExpense = selected ? visible.find((e) => e.id === selected) : null;
  const colorBySelected = selectedExpense
    ? STATUS_GROUPS.find((g) => g.key === "completed" && isFinished?.(selectedExpense))
      || STATUS_GROUPS.find((g) => g.statuses.includes(selectedExpense.status))
    : null;

  return (
    <>
      <div className="space-y-2" data-testid="expense-compact-list">
        {grouped.map((g) => {
          const c = COLOR_CLASSES[g.color];
          const Icon = g.icon;
          const total = g.items.reduce((s, e) => s + (e.amount || 0), 0);
          return (
            <Card
              key={g.key}
              className={`bg-gradient-to-br ${c.bg} ${c.border}`}
            >
              <CardHeader className="pb-1 pt-3">
                <CardTitle className={`${c.text} text-sm flex items-center justify-between gap-2`}>
                  <span className="flex items-center gap-2">
                    <Icon className="w-4 h-4" /> {g.label}
                    <Badge className="bg-white/10 text-white text-[10px]">{g.items.length}</Badge>
                  </span>
                  <span className="text-[11px] font-bold text-white/90">
                    {formatPrice(total)} F
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3 space-y-0.5">
                {/* 🆕 Sous-groupement par MOIS (posting_date || created_at) — style Historique mensuel */}
                {/* 🆕 Fév 2026 — En-tête cliquable (chevron + mini-ventilation top-3 catégories). */}
                {/*     Tous les mois sont REPLIÉS par défaut ; l'utilisateur ouvre ce qu'il veut. */}
                {groupExpensesByMonth(g.items).map((mgroup) => {
                  const compoundKey = `${g.key}::${mgroup.key}`;
                  const monthOpen = openMonths.has(compoundKey);
                  return (
                <React.Fragment key={mgroup.key}>
                  <button
                    type="button"
                    onClick={() => toggleMonth(compoundKey)}
                    className="w-full flex flex-col items-stretch gap-1 px-2 py-1.5 mt-1.5 first:mt-0 rounded bg-slate-900/40 border border-slate-700/50 hover:bg-slate-900/60 hover:border-slate-600 transition-colors text-left"
                    data-testid={`expense-month-header-${mgroup.key}`}
                    aria-expanded={monthOpen}
                  >
                    {/* Ligne 1 : chevron + mois + count + total */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-[10.5px] font-black uppercase tracking-wider text-slate-300 flex items-center gap-1.5 min-w-0 flex-1">
                        {monthOpen
                          ? <ChevronDown className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" strokeWidth={2.4} />
                          : <ChevronRight className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" strokeWidth={2.4} />}
                        <span className="truncate">{monthLabel(mgroup.key)}</span>
                        <span className="text-slate-500 font-normal normal-case tracking-normal whitespace-nowrap">· {mgroup.items.length} achat{mgroup.items.length > 1 ? "s" : ""}</span>
                      </span>
                      <span className="text-[11px] font-black tabular-nums text-white/90 whitespace-nowrap">
                        {formatPrice(mgroup.total)} F
                      </span>
                    </div>
                    {/* Ligne 2 : mini-ventilation top-3 catégories */}
                    {mgroup.top_categories && mgroup.top_categories.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap pl-5">
                        {mgroup.top_categories.map(([cat, val]) => (
                          <span
                            key={cat}
                            className="inline-flex items-center gap-1 text-[9.5px] font-semibold text-slate-400 bg-slate-800/60 border border-slate-700/60 rounded px-1.5 py-0.5 normal-case tracking-normal"
                            title={`${cat} · ${formatPrice(val)} F`}
                            data-testid={`expense-month-cat-${mgroup.key}-${cat}`}
                          >
                            <span className="truncate max-w-[110px]">{cat}</span>
                            <span className="font-mono text-slate-300">{formatPrice(val)}</span>
                          </span>
                        ))}
                        {Object.keys(mgroup.by_category).length > 3 && (
                          <span className="text-[9.5px] text-slate-500 italic normal-case tracking-normal">
                            + {Object.keys(mgroup.by_category).length - 3} autre{Object.keys(mgroup.by_category).length - 3 > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                {monthOpen && mgroup.items.map((e) => {
                  const isSelected = selectionMode && selectedIds?.has?.(e.id);
                  return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => {
                      if (selectionMode && toggleSelection) {
                        toggleSelection(e.id);
                      } else {
                        setSelected(e.id);
                      }
                    }}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 transition-colors text-sm border group ${
                      isSelected ? 'border-purple-500/70 bg-purple-500/10 ring-1 ring-purple-500/40' : 'border-transparent hover:border-white/10'
                    }`}
                    data-testid={`expense-row-${e.id}`}
                  >
                    {selectionMode ? (
                      <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => toggleSelection?.(e.id)}
                        onClick={(ev) => ev.stopPropagation()}
                        className="w-4 h-4 accent-purple-500 cursor-pointer flex-shrink-0"
                        data-testid={`select-expense-${e.id}`}
                      />
                    ) : (
                      <span className="text-slate-500 group-hover:text-amber-400 select-none">—</span>
                    )}
                    <span className="text-slate-200 flex-1 truncate">
                      {e.description || "(sans description)"}
                    </span>
                    {e.is_group && e.items?.length > 0 && (
                      <span className="text-[10px] text-indigo-300 bg-indigo-500/20 border border-indigo-500/30 rounded px-1.5 py-0.5 whitespace-nowrap">
                        📦 {e.items.length}
                      </span>
                    )}
                    {e.is_regularization && (
                      <span
                        className="text-[10px] text-violet-100 bg-violet-500/25 border border-violet-500/50 rounded px-1.5 py-0.5 whitespace-nowrap"
                        title={`Régularisation${e.regularization_target_month ? " " + e.regularization_target_month : ""}${e.regularization_reason ? "\nMotif : " + e.regularization_reason : ""}`}
                        data-testid={`regularization-badge-${e.id}`}
                      >
                        🕒 Régul.{e.regularization_target_month ? ` ${e.regularization_target_month}` : ""}
                      </span>
                    )}
                    {e.assigned_date && (
                      <span className="text-[10px] text-purple-200 bg-purple-500/20 border border-purple-500/30 rounded px-1.5 py-0.5 whitespace-nowrap">
                        📌 {e.assignment_precision === 'month' ? e.assigned_date.slice(0,7) : e.assigned_date}
                      </span>
                    )}
                    <span className="text-amber-400 font-bold whitespace-nowrap">
                      {formatPrice(e.amount)} F
                    </span>
                    <span className="text-slate-500 text-xs whitespace-nowrap w-10 text-right">
                      {formatDateLine(e)}
                    </span>
                  </button>
                  );
                })}
                </React.Fragment>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selectedExpense} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[92vh] overflow-y-auto">
          {selectedExpense && (() => {
            const e = selectedExpense;
            const finished = isFinished?.(e);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className={`${colorBySelected ? COLOR_CLASSES[colorBySelected.color].text : 'text-amber-400'} text-lg flex items-center gap-2`}>
                    {e.is_group ? '📦' : '🛒'} {e.description || "Détail de l'achat"}
                  </DialogTitle>
                  <DialogDescription className="text-slate-400 text-xs flex flex-wrap gap-x-3 gap-y-1">
                    <span>Statut : <strong className="text-white">{finished ? 'terminé' : (e.status || '—')}</strong></span>
                    {e.category && <span>Catégorie : <strong className="text-white">{e.category}</strong></span>}
                    {e.requested_by && <span>Demandé par : <strong className="text-white">{e.requested_by}</strong></span>}
                    {e.is_regularization && (
                      <span className="w-full mt-2 p-2 rounded bg-violet-500/15 border border-violet-500/40 text-violet-100 flex flex-col gap-0.5">
                        <span className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wide">
                          🕒 Régularisation rétroactive
                          {e.regularization_target_month && (
                            <span className="ml-1 bg-violet-500/40 px-1.5 py-0.5 rounded text-[10px] normal-case font-mono">
                              {e.regularization_target_month}
                            </span>
                          )}
                        </span>
                        {e.posting_date && (
                          <span className="text-[11px]">
                            Date comptable : <strong className="text-white">{e.posting_date}</strong>
                            {e.created_at && (
                              <span className="text-violet-200/70 ml-2">
                                (saisi le {(() => { try { return format(parseISO(e.created_at), "dd/MM/yyyy HH:mm", { locale: fr }); } catch { return e.created_at.slice(0,16); } })()})
                              </span>
                            )}
                          </span>
                        )}
                        {e.regularization_reason && (
                          <span className="text-[11px] italic">
                            Motif : « <strong className="not-italic text-white">{e.regularization_reason}</strong> »
                          </span>
                        )}
                      </span>
                    )}
                    {e.created_at && !e.is_regularization && (() => {
                      try { return <span>Créé le : <strong className="text-white">{format(parseISO(e.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}</strong></span>; }
                      catch { return null; }
                    })()}
                    {e.completed_at && (() => {
                      try { return <span>Terminé le : <strong className="text-white">{format(parseISO(e.completed_at), "dd/MM/yyyy HH:mm", { locale: fr })}</strong></span>; }
                      catch { return null; }
                    })()}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 pt-2">
                  {/* Montant + supplier */}
                  <div className="bg-slate-800/40 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm text-slate-300">
                      {e.supplier && <span className="mr-3">Fournisseur : <strong className="text-white">{e.supplier}</strong></span>}
                      {e.planned_date && <span>Prévu : <strong className="text-white">{e.planned_date}</strong></span>}
                    </div>
                    <div className="text-2xl font-bold text-amber-400">
                      {formatPrice(e.amount)} F
                    </div>
                  </div>

                  {/* Items if grouped */}
                  {e.is_group && e.items?.length > 0 && (
                    <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/40">
                      <p className="text-slate-300 text-xs uppercase font-bold mb-2">Détails de la liste ({e.items.length} article{e.items.length > 1 ? 's' : ''})</p>
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {e.items.map((it, idx) => (
                          <div key={idx} className={`flex items-center justify-between text-sm py-1 px-2 rounded ${it.struck ? 'opacity-50 line-through' : 'bg-slate-900/40'}`}>
                            <span className="text-white">{idx + 1}. {it.description}</span>
                            <span className="text-slate-400">
                              {it.quantity} × {formatPrice(it.unit_price)} = <strong className="text-amber-300">{formatPrice(it.amount)} F</strong>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes admin */}
                  {e.admin_notes && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                      <p className="text-amber-300 text-xs uppercase font-bold mb-1">Note admin</p>
                      <p className="text-amber-100 text-sm whitespace-pre-wrap">{e.admin_notes}</p>
                    </div>
                  )}

                  {/* Actions selon statut */}
                  <div className="border-t border-slate-700 pt-3 flex flex-wrap gap-2">
                    {/* PENDING / ADMIN_REVIEW : admin → valider/réviser/rejeter */}
                    {isAdmin && (e.status === 'pending' || e.status === 'admin_review') && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => { updateExpense?.(e.id, { status: 'approved' }); setSelected(null); }}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`detail-approve-${e.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { openReviseModal?.(e); setSelected(null); }}
                          className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
                          data-testid={`detail-revise-${e.id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" /> Demander révision
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { updateExpense?.(e.id, { status: 'rejected' }); setSelected(null); }}
                          className="border-red-500/50 text-red-300 hover:bg-red-500/10"
                          data-testid={`detail-reject-${e.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Rejeter
                        </Button>
                      </>
                    )}

                    {/* REVISION_REQUESTED : Responsable Op. & Log → corriger */}
                    {!isAdmin && e.status === 'revision_requested' && (
                      <Button
                        size="sm"
                        onClick={() => { openExpenseForEdit?.(e); setSelected(null); }}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid={`detail-edit-revision-${e.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" /> Corriger & renvoyer
                      </Button>
                    )}

                    {/* APPROVED : convertir en PO / marquer acheté */}
                    {e.status === 'approved' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => { convertExpenseToPO?.(e); setSelected(null); }}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`detail-convert-po-${e.id}`}
                        >
                          <Truck className="w-4 h-4 mr-1" /> Bon de commande
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { receiveExpenseStock?.(e.id); setSelected(null); }}
                          className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                          data-testid={`detail-receive-stock-${e.id}`}
                        >
                          <PackagePlus className="w-4 h-4 mr-1" /> Réceptionner stock
                        </Button>
                      </>
                    )}

                    {/* Impressions toujours dispo */}
                    {printSingleExpenseTicket && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => printSingleExpenseTicket(e)}
                        className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                        data-testid={`detail-print-ticket-${e.id}`}
                      >
                        <Receipt className="w-4 h-4 mr-1" /> Ticket 80mm
                      </Button>
                    )}
                    {printExpensePDF && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => printExpensePDF(e)}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        <Printer className="w-4 h-4 mr-1" /> PDF A4
                      </Button>
                    )}

                    {/* Edit / Delete (admin) */}
                    {isAdmin && !finished && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { openExpenseForEdit?.(e); setSelected(null); }}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          <Edit2 className="w-4 h-4 mr-1" /> Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { if (window.confirm("Supprimer définitivement cette demande ?")) { deleteExpense?.(e.id); setSelected(null); } }}
                          className="border-red-500/50 text-red-300 hover:bg-red-500/10"
                          data-testid={`detail-delete-${e.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                        </Button>
                      </>
                    )}

                    {/* 🆕 Archiver individuellement (admin) */}
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          if (!window.confirm("Archiver cet achat ? Il sera déplacé dans le dossier Archives (réversible).")) return;
                          try {
                            await axios.patch(`${API}/expenses/${e.id}/archive`, {
                              archived: true,
                              reason: "manual",
                              actor_name: currentUser?.full_name || currentUser?.username || "Admin",
                              actor_role: "admin",
                            });
                            toast.success("Achat archivé");
                            setSelected(null);
                            if (typeof fetchExpenses === "function") fetchExpenses();
                            window.dispatchEvent(new CustomEvent("expenses-archived", { detail: { count: 1, kind: "manual" } }));
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Erreur pendant l'archivage");
                          }
                        }}
                        className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
                        data-testid={`detail-archive-${e.id}`}
                        title="Déplacer cet achat dans le dossier Archives"
                      >
                        <Archive className="w-4 h-4 mr-1" /> Archiver
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExpenseCompactList;
